import { useEffect, useState } from "react";
import { ArrowRightLeft } from "lucide-react";
import { formatDateTimeToDisplay } from "@/utils/dateUtils";

interface EventTime {
  id: string;
  date: string;
  time: string;
}

interface Neighborhood {
  id: string;
  name: string;
}

export type BookingStatus = "pending" | "active" | "cancelled" | "cant_be_seated";

interface EditReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Submits booking-level field changes (event time / neighborhood / status).
   *  Table reassignment is handled separately via `onReassign`. */
  onSubmit: (data: {
    eventTimeId?: string;
    neighborhoodId?: string;
    status?: BookingStatus;
  }) => void;
  eventTimes: EventTime[];
  neighborhoods: Neighborhood[];
  /** Optional — when provided, renders a "Reassign user" button that defers
   *  to the unified ReassignmentDialog. */
  onReassign?: () => void;
  initialData: {
    userName: string;
    userEmail: string;
    eventTimeId: string;
    neighborhoodId: string;
    status?: BookingStatus;
    bookingId: string;
  };
  showStatus?: boolean;
  isLoading?: boolean;
}

export function EditReservationModal({
  isOpen,
  onClose,
  onSubmit,
  eventTimes,
  neighborhoods,
  onReassign,
  initialData,
  showStatus = false,
  isLoading = false,
}: EditReservationModalProps) {
  const [eventTimeId, setEventTimeId] = useState("");
  const [neighborhoodId, setNeighborhoodId] = useState("");
  const [status, setStatus] = useState<BookingStatus>("active");

  useEffect(() => {
    if (isOpen && initialData) {
      setEventTimeId(initialData.eventTimeId);
      setNeighborhoodId(initialData.neighborhoodId);
      setStatus(initialData.status ?? "active");
    }
  }, [isOpen, initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const updates: {
      eventTimeId?: string;
      neighborhoodId?: string;
      status?: BookingStatus;
    } = {};

    if (eventTimeId !== initialData.eventTimeId) updates.eventTimeId = eventTimeId;
    if (neighborhoodId !== initialData.neighborhoodId) updates.neighborhoodId = neighborhoodId;
    if (showStatus && status !== initialData.status) updates.status = status;

    if (Object.keys(updates).length > 0) {
      onSubmit(updates);
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  if (!isOpen) return null;

  const hasChanges =
    eventTimeId !== initialData.eventTimeId ||
    neighborhoodId !== initialData.neighborhoodId ||
    (showStatus && status !== initialData.status);

  return (
    <div className="modal modal-open" onClick={handleClose}>
      <div className="modal-box max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-xl mb-2">Edit Reservation</h3>
        <p className="text-sm text-base-content/60 mb-4">
          {initialData.userName} ({initialData.userEmail})
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Event Time *</span>
            </label>
            <select
              value={eventTimeId}
              onChange={(e) => setEventTimeId(e.target.value)}
              className="select select-bordered w-full"
              disabled={isLoading}
              required
            >
              <option value="">Select an event time</option>
              {eventTimes.map((event) => (
                <option key={event.id} value={event.id}>
                  {formatDateTimeToDisplay(event.date, event.time)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Neighborhood *</span>
            </label>
            <select
              value={neighborhoodId}
              onChange={(e) => setNeighborhoodId(e.target.value)}
              className="select select-bordered w-full"
              disabled={isLoading}
              required
            >
              <option value="">Select a neighborhood</option>
              {neighborhoods.map((neighborhood) => (
                <option key={neighborhood.id} value={neighborhood.id}>
                  {neighborhood.name}
                </option>
              ))}
            </select>
          </div>

          {showStatus && (
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Status *</span>
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as BookingStatus)}
                className="select select-bordered w-full"
                disabled={isLoading}
                required
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="cant_be_seated">Can't be seated</option>
              </select>
            </div>
          )}

          {onReassign && (
            <div className="form-control">
              <button
                type="button"
                onClick={onReassign}
                disabled={isLoading}
                className="btn btn-outline btn-sm w-full justify-start"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Reassign user (table / can't be seated)…
              </button>
            </div>
          )}

          {hasChanges && (
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 text-sm">
              <p className="font-medium text-warning-content">Note:</p>
              <p className="text-warning-content/80">
                Changing the event time or neighborhood will remove any existing table assignment for this user.
              </p>
            </div>
          )}

          <div className="modal-action">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !eventTimeId || !neighborhoodId}
              className="btn btn-primary"
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
